import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  loadBilling,
  reloadCredits,
  selectBillingBalance,
  selectBillingCreditItems,
  selectBillingCreditsReloading,
  selectBillingLoadingState,
  selectBillingPlans,
  selectBillingTransactions
} from 'Flux/Slice/Billing/BillingSlice.js'
import { useGetAuthenticatedUser } from 'Flux/Selector/index.js'
import { hasActiveSubscription } from 'Model/User/IUser.js'
import { Environment } from 'environment.js'
import { PlanSelection } from './PlanSelection.js'
import { CreditBalanceCard } from './CreditBalanceCard.js'
import { TransactionHistory } from './TransactionHistory.js'
import { useCheckoutReturn } from './useCheckoutReturn.js'
import { useSessionSync } from './useSessionSync.js'
import { useRechargePolling } from './useRechargePolling.js'

export const BillingPage = () => {
  const user = useGetAuthenticatedUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()

  const plans = useSelector(selectBillingPlans)
  const creditItems = useSelector(selectBillingCreditItems)
  const balance = useSelector(selectBillingBalance)
  const transactions = useSelector(selectBillingTransactions)
  const loadingState = useSelector(selectBillingLoadingState)
  const creditsReloading = useSelector(selectBillingCreditsReloading)

  const [awaitingSync, setAwaitingSync] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [rechargePending, setRechargePending] = useState(false)

  const rechargeInitialBalance = useRef<number | null>(null)
  const isCheckoutReturn = searchParams.get('checkout') === 'success'
  const isRechargeReturn = searchParams.get('recharge') === 'success'

  // Balance + history are only fetched for orgs that have a subscription / chargebee customer.
  const includeCredits = !!(user && (hasActiveSubscription(user.org) || user.org.chargebeeCustomerId))

  // Initial load (and reload when the org's plan/subscription changes).
  useEffect(() => {
    dispatch(loadBilling({ includeCredits }))
  }, [dispatch, includeCredits])

  // Periodic refresh as a fallback for when the websocket credit event isn't delivered.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') dispatch(loadBilling({ includeCredits }))
    }, 300_000)
    return () => clearInterval(interval)
  }, [dispatch, includeCredits])

  const reload = useCallback(() => {
    dispatch(reloadCredits())
  }, [dispatch])

  useCheckoutReturn({
    user,
    balance,
    isCheckoutReturn,
    isRechargeReturn,
    setSearchParams,
    reload,
    setAwaitingSync,
    setRechargePending,
    rechargeInitialBalance
  })

  useSessionSync({
    user,
    isCheckoutReturn,
    isRechargeReturn,
    syncing,
    setSyncing
  })

  useRechargePolling({
    user,
    awaitingSync,
    rechargePending,
    balance,
    setAwaitingSync,
    setRechargePending,
    reload,
    rechargeInitialBalance
  })

  if (!user) return null

  const loading = loadingState === 'initializing' || loadingState === 'loading'
  if ((loading || syncing) && !awaitingSync) {
    return (
      <div className="flex justify-center items-center py-24">
        <span className="loading loading-spinner loading-lg" aria-label="Loading billing information" />
      </div>
    )
  }

  if (loadingState === 'error') {
    return (
      <div className="max-w-5xl mx-auto py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Billing</h1>
        <p className="text-gray-600">Billing is not currently available. Please try again later or contact support.</p>
        <button className="btn btn-primary mt-6" onClick={() => dispatch(loadBilling({ includeCredits }))}>
          Retry
        </button>
      </div>
    )
  }

  const subscribed = hasActiveSubscription(user.org)
  const showBillingBlocker = !!user.org.isBillingMissing
  const showPaymentIssueWarning = !!user.org.lastPaymentIssueAt && !showBillingBlocker
  const selfServicePortalUrl = `${Environment.API_URL}/v1/chargebee/self-service-portal`

  const trialEndsAt = user.org.chargebeeTrialEndsAt ? new Date(user.org.chargebeeTrialEndsAt) : null
  const trialActive = trialEndsAt !== null && trialEndsAt.getTime() > Date.now()
  const trialDaysLeft = trialActive ? Math.max(0, Math.ceil((trialEndsAt!.getTime() - Date.now()) / 86_400_000)) : 0

  return (
    <div data-testid="billing-page" className="max-w-5xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">Billing</h1>

      {showBillingBlocker && (
        <div className="alert alert-error shadow-sm">
          <div className="flex flex-col items-start gap-3">
            <div>
              <h2 className="font-bold">Billing block active</h2>
              <p>
                Your organization is currently blocked for billing reasons. Features may not work. Please contact support and review your
                billing details.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a className="btn btn-sm btn-primary" href={selfServicePortalUrl} target="_blank" rel="noreferrer">
                Open billing portal
              </a>
              <a className="btn btn-sm btn-outline" href="mailto:support@spcfy.io">
                Contact support
              </a>
            </div>
          </div>
        </div>
      )}

      {showPaymentIssueWarning && (
        <div className="alert alert-warning shadow-sm">
          <div className="flex flex-col items-start gap-3">
            <div>
              <h2 className="font-bold">Payment issue detected</h2>
              <p>A payment issue was detected for your organization. Access may soon be restricted unless this is resolved.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a className="btn btn-sm btn-primary" href={selfServicePortalUrl} target="_blank" rel="noreferrer">
                Resolve billing issue
              </a>
            </div>
          </div>
        </div>
      )}

      {trialActive && (
        <div className="alert alert-info shadow-sm" data-testid="trial-banner">
          <div className="flex flex-col items-start gap-1">
            <h2 className="font-bold">
              Free trial — {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'} left
            </h2>
            <p>
              Your trial ends on{' '}
              <strong>{trialEndsAt!.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}</strong>. After that, your
              subscription automatically switches to the standard plan.
            </p>
          </div>
        </div>
      )}

      {awaitingSync ? (
        <div className="bg-base-200 rounded-lg p-8 text-center">
          <span className="loading loading-spinner loading-md" />
          <p className="mt-3 font-medium">Setting up your subscription...</p>
          <p className="text-sm text-gray-500">This usually takes just a moment.</p>
        </div>
      ) : (
        <>
          {rechargePending && (
            <div className="alert alert-info">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Your credit purchase is being processed. Your balance will update shortly.</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setRechargePending(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-4 h-4 stroke-current">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          <PlanSelection plans={plans} creditItems={creditItems} org={user.org} subscribed={subscribed} />
          {(subscribed || (balance?.balance ?? 0) > 0) && (
            <>
              <CreditBalanceCard balance={balance} creditItem={creditItems[0] ?? null} />
              <TransactionHistory
                transactions={transactions}
                onReload={subscribed ? reload : undefined}
                reloading={creditsReloading}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
